import {
  ACQUIRER_TERMINAL_TAB_FILTER_KEYS,
  MERCHANT_TERMINAL_TAB_FILTER_KEYS,
  TERMINAL_TAB_FILTER_KEYS,
} from '../constants/constants';
import { GRID_LIST_FETCH_SIZE } from '../../../components/shared/agGridCells';
import { mapAuditFields } from '../../../lib/auditColumnDefs';
import { toApiEntityStatus } from '../../../utils/status';
import type {
  AcquirerTerminalRow,
  CountryLookup,
  MerchantTerminalRow,
  TerminalInventoryListRequest,
  TerminalMappingListRequest,
  TerminalRow,
  TerminalsColumnFilterKey,
  TerminalsColumnFilters,
} from '../models/types';

export function normalizeCountryLookups(raw: unknown): CountryLookup[] {
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as { data?: unknown[] })?.data)
      ? (raw as { data: unknown[] }).data
      : [];

  return list
    .map((item) => {
      const row = item as Record<string, unknown>;
      return {
        autoId: Number(row.autoId ?? row.id ?? 0),
        countryName: String(row.countryName ?? row.name ?? '').trim(),
        countryFlag: String(row.countryFlag ?? '').trim().toUpperCase(),
        currency: String(row.currency ?? '').trim().toUpperCase(),
      };
    })
    .filter((country) => country.autoId > 0 && country.countryName.length > 0);
}

export function buildCountryById(lookups: CountryLookup[]): Record<number, CountryLookup> {
  return lookups.reduce<Record<number, CountryLookup>>((acc, country) => {
    acc[country.autoId] = country;
    return acc;
  }, {});
}

export function buildCurrencyById(lookups: CountryLookup[]): Record<number, string> {
  return lookups.reduce<Record<number, string>>((acc, country) => {
    if (country.autoId > 0 && country.currency) {
      acc[country.autoId] = country.currency;
    }
    return acc;
  }, {});
}

export function buildCountryIdsByCurrency(
  lookups: CountryLookup[],
): Record<string, number[]> {
  return lookups.reduce<Record<string, number[]>>((acc, country) => {
    if (country.autoId <= 0 || !country.currency) return acc;
    const code = country.currency.toUpperCase();
    const existing = acc[code] ?? [];
    if (!existing.includes(country.autoId)) {
      acc[code] = [...existing, country.autoId];
    }
    return acc;
  }, {});
}

export function resolveCurrencyCodesFromCountryIds(
  countryIds: number[],
  currencyById: Record<number, string>,
): string[] {
  const codes = new Set<string>();
  countryIds.forEach((id) => {
    const currency = currencyById[id];
    if (currency) codes.add(currency);
  });
  return [...codes].sort((a, b) => a.localeCompare(b));
}

export function collectTerminalCurrencyFilterOptions(
  rows: Array<{ currencies?: number[] }>,
  currencyById: Record<number, string>,
): Array<{ value: string; label: string }> {
  const currencies = new Set<string>();
  rows.forEach((row) => {
    resolveCurrencyCodesFromCountryIds(row.currencies ?? [], currencyById).forEach((code) => {
      if (code) currencies.add(code.toUpperCase());
    });
  });
  return Array.from(currencies)
    .sort((a, b) => a.localeCompare(b))
    .map((currency) => ({ value: currency, label: currency }));
}

function expandCurrencyFilterToCountryIds(
  values: string[],
  countryIdsByCurrency?: Record<string, number[]>,
): string[] {
  const countryIds = new Set<string>();
  values.forEach((value) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (/^\d+$/.test(trimmed)) {
      countryIds.add(trimmed);
      return;
    }
    const ids = countryIdsByCurrency?.[trimmed.toUpperCase()] ?? [];
    ids.forEach((id) => {
      if (id > 0) countryIds.add(String(id));
    });
  });
  return Array.from(countryIds);
}

export function formatCurrencyCodesLabel(
  countryIds: number[],
  currencyById: Record<number, string>,
): string {
  const codes = resolveCurrencyCodesFromCountryIds(countryIds, currencyById);
  return codes.length > 0 ? codes.join(', ') : '—';
}

export function resolveDisplayName(
  apiName: string | undefined,
  lookupById: Record<number, string> | undefined,
  id: number,
): string {
  const trimmed = String(apiName ?? '').trim();
  if (trimmed) return trimmed;
  if (id && lookupById?.[id]) return lookupById[id];
  return '—';
}

export function parseNumberArray(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => Number(item)).filter((n) => !Number.isNaN(n));
}

export function parseStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item)).filter((s) => s.length > 0);
}

export function parseExtraFields(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object') return {};
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, string>>(
    (acc, [key, val]) => {
      acc[key] = String(val ?? '');
      return acc;
    },
    {},
  );
}

export function formatExtraFieldsLabel(fields: Record<string, string>): string {
  const entries = Object.entries(fields);
  if (entries.length === 0) return '—';
  return entries.map(([key, value]) => `${key}: ${value}`).join(' · ');
}

export function extractApiList(res: unknown): Record<string, unknown>[] {
  if (!res || typeof res !== 'object') return [];
  const record = res as Record<string, unknown>;
  if (record.success === false) return [];
  const data = record.data;
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (data && typeof data === 'object') {
    const content = (data as Record<string, unknown>).content;
    if (Array.isArray(content)) return content as Record<string, unknown>[];
  }
  return [];
}

export function mapTerminalRow(item: Record<string, unknown>): TerminalRow {
  return {
    id: Number(item.mappingId ?? item.id),
    merchantId: Number(item.merchantId ?? 0),
    merchantTerminalId: Number(item.merchantTerminalId ?? 0),
    acquirerId: Number(item.acquirerId ?? 0),
    acquirerTerminalId: Number(item.acquirerTerminalId ?? 0),
    merchantTerminalName: String(item.merchantTerminalName ?? '—'),
    acquirerTerminalName: String(item.acquirerTerminalName ?? '—'),
    merchantName: String(item.merchantName ?? item.MerchantName ?? '').trim(),
    acquirerName: String(item.acquirerName ?? item.AcquirerName ?? '').trim(),
    status: String(item.status ?? 'UNKNOWN'),
    volume: Number(item.totalVolume ?? item.volume ?? 0),
    trafficId: item.trafficId != null ? Number(item.trafficId) : undefined,
    priority: item.priority != null ? Number(item.priority) : undefined,
    priorityRoutingEnabled: Boolean(item.priorityRoutingEnabled),
    collectCustomerDetailsOnCheckout: Boolean(item.collectCustomerDetailsOnCheckout),
    testDummyValues: Boolean(item.testDummyValues),
    apmSchemes: Array.isArray(item.apmSchemes) ? (item.apmSchemes as unknown[]).map(String) : [],
    ...mapAuditFields(item),
  };
}

export function mapMerchantTerminalRow(item: Record<string, unknown>): MerchantTerminalRow {
  return {
    terminalId: Number(item.terminalId ?? item.id ?? item.autoId ?? item.merchantTerminalId ?? 0),
    name: String(item.name ?? item.terminalName ?? item.merchantTerminalName ?? '—'),
    merchantId: Number(item.merchantId ?? 0),
    merchantName: String(item.merchantName ?? item.MerchantName ?? '').trim(),
    countryId: Number(item.countryId ?? 0),
    countryName: String(item.countryName ?? '').trim(),
    currencies: parseNumberArray(item.currencies),
    flowId: Number(item.flowId ?? 0),
    flowName: String(item.type ?? item.flowType ?? item.flowName ?? '').trim(),
    industryId: Number(item.industryId ?? 0),
    industryName: String(item.industryName ?? '').trim(),
    trafficId: Number(item.trafficId ?? 0),
    trafficName: String(item.trafficName ?? item.trafficType ?? '').trim(),
    notes: String(item.notes ?? ''),
    successTransactionVolume: Number(item.successTransactionVolume ?? 0),
    status: item.status != null ? String(item.status) : undefined,
    ...mapAuditFields(item),
  };
}

export function mapAcquirerTerminalRow(item: Record<string, unknown>): AcquirerTerminalRow {
  const extraFields = parseExtraFields(item.extraFields);
  return {
    terminalId: Number(item.terminalId ?? item.id ?? item.autoId ?? item.acquirerTerminalId ?? 0),
    name: String(item.name ?? item.terminalName ?? item.acquirerTerminalName ?? '—'),
    acquirerId: Number(item.acquirerId ?? 0),
    acquirerName: String(item.acquirerName ?? item.AcquirerName ?? '').trim(),
    countryId: Number(item.countryId ?? 0),
    countryName: String(item.countryName ?? '').trim(),
    currencies: parseNumberArray(item.currencies),
    extraFields,
    flowId: Number(item.flowId ?? 0),
    flowName: String(item.type ?? item.flowType ?? item.flowName ?? '').trim(),
    industryId: Number(item.industryId ?? 0),
    industryName: String(item.industryName ?? '').trim(),
    trafficId: Number(item.trafficId ?? 0),
    trafficName: String(item.trafficName ?? item.trafficType ?? '').trim(),
    successTransactionVolume: Number(item.successTransactionVolume ?? 0),
    status: item.status != null ? String(item.status) : undefined,
    enforceRoutingConstraints: Boolean(item.enforceRoutingConstraints),
    allowedCountryCodes: parseStringArray(item.allowedCountryCodes),
    minAmount: item.minAmount != null ? Number(item.minAmount) : null,
    maxAmount: item.maxAmount != null ? Number(item.maxAmount) : null,
    ...mapAuditFields(item),
  };
}

export function formatMappingIdLabel(id: number) {
  return id != null ? `Mapping #${id}` : '—';
}

export function formatMerchantTerminalIdLabel(id: number) {
  return id ? `Terminal #${id}` : '—';
}

export function formatAcquirerTerminalIdLabel(id: number) {
  return id ? `Terminal #${id}` : '—';
}

export function formatMappingPriorityLabel(priority?: number | null) {
  return priority ? `P${priority}` : '—';
}

export function formatSuccessTransactionVolume(value: number | null | undefined) {
  const amount = Number(value ?? 0);
  return amount > 0 ? amount.toLocaleString() : '—';
}

const TERMINALS_COLUMN_FILTER_KEY_SET: Record<TerminalsColumnFilterKey, true> = {
  merchantTerminal: true,
  acquirerTerminal: true,
  mappingId: true,
  merchantTerminalId: true,
  acquirerTerminalId: true,
  merchant: true,
  acquirer: true,
  volume: true,
  status: true,
  priority: true,
  name: true,
  country: true,
  currencies: true,
  flow: true,
  industry: true,
  traffic: true,
  notes: true,
  createdAt: true,
};

/**
 * Translate AG Grid's native column filterModel into the same shape the Filters drawer
 * stores in `columnFilters` / feeds the terminals list requests — so header-funnel filters
 * and the drawer both produce identical `request.filters` payloads.
 *
 * Text filters → `{ [colId]: [filterText] }`.
 * Date column (`agDateColumnFilter` on createdAt) → `createdAt: [from, to]` yyyy-MM-dd.
 */
export function mapAgGridFilterModelToTerminalsFilters(
  filterModel: Record<string, unknown> | null | undefined,
): Partial<TerminalsColumnFilters> {
  const columnFilters: Partial<TerminalsColumnFilters> = {};
  if (!filterModel) return columnFilters;

  for (const [colId, raw] of Object.entries(filterModel)) {
    if (!raw || typeof raw !== 'object') continue;
    const model = raw as {
      filter?: unknown;
      dateFrom?: string;
      dateTo?: string;
    };

    if (colId === 'createdAt') {
      if (typeof model.dateFrom === 'string' && model.dateFrom.length >= 10) {
        const from = model.dateFrom.slice(0, 10);
        const to = (model.dateTo ?? model.dateFrom).slice(0, 10);
        columnFilters.createdAt = [from, to];
      }
      continue;
    }

    if (typeof model.filter !== 'string' || !model.filter.trim()) continue;
    if (colId in TERMINALS_COLUMN_FILTER_KEY_SET) {
      columnFilters[colId as TerminalsColumnFilterKey] = [model.filter];
    }
  }

  return columnFilters;
}

function buildTerminalInventoryRequest(options: {
  page?: number;
  size?: number;
  search: string;
  columnFilters: TerminalsColumnFilters;
  filterKeys: TerminalsColumnFilterKey[];
  countryIdsByCurrency?: Record<string, number[]>;
  export?: boolean;
}): TerminalInventoryListRequest {
  const request: TerminalInventoryListRequest = {
    page: options.page ?? 0,
    size: options.size ?? GRID_LIST_FETCH_SIZE,
  };

  const query = options.search.trim();
  if (query) {
    request.search = query;
  }

  const filters = Object.fromEntries(
    options.filterKeys.flatMap((key): Array<[TerminalsColumnFilterKey, string[]]> => {
      const values = options.columnFilters[key];
      if (!values?.length) return [];
      if (key === 'currencies') {
        const countryIds = expandCurrencyFilterToCountryIds(
          values,
          options.countryIdsByCurrency,
        );
        return countryIds.length ? [[key, countryIds]] : [];
      }
      return [[key, values]];
    }),
  );

  if (Object.keys(filters).length > 0) {
    request.filters = filters;
  }

  if (options.export) {
    request.export = true;
  }

  return request;
}

export function buildMappingsRequest(options: {
  page?: number;
  size?: number;
  filterStatus: string;
  search: string;
  columnFilters: TerminalsColumnFilters;
  export?: boolean;
}): TerminalMappingListRequest {
  const request: TerminalMappingListRequest = {
    page: options.page ?? 0,
    size: options.size ?? GRID_LIST_FETCH_SIZE,
  };

  if (options.filterStatus !== 'All') {
    request.status = toApiEntityStatus(options.filterStatus);
  }

  const query = options.search.trim();
  if (query) {
    request.search = query;
  }

  const filters = Object.fromEntries(
    TERMINAL_TAB_FILTER_KEYS.flatMap((key) => {
      const values = options.columnFilters[key];
      return values?.length ? [[key, values]] : [];
    }),
  );

  if (Object.keys(filters).length > 0) {
    request.filters = filters;
  }

  if (options.export) {
    request.export = true;
  }

  return request;
}

export function buildMerchantTerminalsRequest(options: {
  page?: number;
  size?: number;
  search: string;
  columnFilters: TerminalsColumnFilters;
  countryIdsByCurrency?: Record<string, number[]>;
  export?: boolean;
}) {
  return buildTerminalInventoryRequest({
    ...options,
    filterKeys: MERCHANT_TERMINAL_TAB_FILTER_KEYS,
  });
}

export function buildAcquirerTerminalsRequest(options: {
  page?: number;
  size?: number;
  search: string;
  columnFilters: TerminalsColumnFilters;
  countryIdsByCurrency?: Record<string, number[]>;
  export?: boolean;
}) {
  return buildTerminalInventoryRequest({
    ...options,
    filterKeys: ACQUIRER_TERMINAL_TAB_FILTER_KEYS,
  });
}

export function buildEditTerminalState(
  target: Extract<
    import('../models/types').DeleteTarget,
    { type: 'merchant' } | { type: 'acquirer' }
  >,
): import('../models/types').CreateTerminalEditState {
  return {
    mode: 'edit',
    terminalType: target.type,
    terminalId: target.row.terminalId,
  };
}
